import { useCallback, useMemo } from 'react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const GroupOptions = () => {
  const { colDefs, setColDefs, gridApi } = useTableContext();

  const currentGroup = useMemo(() => {
    if (!colDefs?.length) return null;
    return colDefs.find((c) => c.rowGroupIndex === 0) ?? colDefs.find((c) => c.rowGroup) ?? null;
  }, [colDefs]);

  const clearGrouping = useCallback(() => {
    if (gridApi?.applyColumnState) {
      gridApi.applyColumnState({
        defaultState: { rowGroup: false, rowGroupIndex: null },
        state: []
      });
    }
    setColDefs((prev) => prev.map((c) => ({ ...c, rowGroup: false, rowGroupIndex: null })));
  }, [gridApi, setColDefs]);

  const groupByField = useCallback(
    (field) => {
      if (!field) return clearGrouping();
      if (gridApi?.applyColumnState) {
        gridApi.applyColumnState({
          defaultState: { rowGroup: false, rowGroupIndex: null },
          state: [{ colId: field, rowGroup: true, rowGroupIndex: 0 }]
        });
      }
      setColDefs((prev) =>
        prev.map((c) => ({
          ...c,
          rowGroup: c.field === field,
          rowGroupIndex: c.field === field ? 0 : null
        }))
      );
    },
    [gridApi, setColDefs, clearGrouping]
  );

  const options = useMemo(() => {
    const columnOptions = colDefs.map((col) => ({
      label: col.headerName,
      value: col.field,
      onClick: () => groupByField(col.field)
    }));
    return [
      { label: 'No grouping', value: null, onClick: () => clearGrouping() },
      ...columnOptions
    ];
  }, [colDefs, groupByField, clearGrouping]);

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <Typography icon="grid-4-mono" iconColor="neutral-700" variant="label" size="medium">
        Group
      </Typography>
      <Popover
        position="left"
        panelClassName="p-0 border border-neutral-100 shadow-spread-medium rounded border-solid gap-y-1"
        optionGroupLabelClassName="h-10 px-4 py-2"
        options={options}
        customButtonTrigger={({ open }) => (
          <Button
            size="xs"
            text={currentGroup ? currentGroup.headerName : 'No grouping'}
            variant="tertiary"
            iconColor="neutral"
            iconRight={open ? 'chevron-up-linear' : 'chevron-down-linear'}
            iconShade={700}
            className={cn('whitespace-nowrap', open && 'bg-neutral-100')}
          />
        )}
      />
    </div>
  );
};

export default GroupOptions;
